import type { Card, Word } from '@/types/model';

export const SAVED_WORDS_CACHE_SCHEMA_VERSION = 1;

const SAVED_WORDS_DATABASE_NAME = 'issen_saved_words_v1';
const SAVED_WORDS_DATABASE_VERSION = 1;
const SAVED_WORDS_OBJECT_STORE = 'snapshots';
const SAVED_WORDS_BROADCAST_CHANNEL = 'issen-saved-words';

export type SavedWordsCacheRecord = {
  schemaVersion: number;
  cacheKey: string;
  userId: string;
  language: string;
  words: Word[];
  cards: Card[];
  maxAddedTime: number;
  updatedAtMs: number;
};

export interface SavedWordsCacheStorageAdapter {
  read(cacheKey: string): Promise<SavedWordsCacheRecord | null>;
  write(record: SavedWordsCacheRecord): Promise<void>;
  remove(cacheKey: string): Promise<void>;
  clear(): Promise<void>;
}

export type SavedWordsCacheBroadcastMessage =
  | {
      type: 'snapshot';
      sourceId: string;
      record: SavedWordsCacheRecord;
    }
  | {
      type: 'clear';
      sourceId: string;
      clearedAtMs: number;
    };

export interface SavedWordsCacheBroadcastAdapter {
  publish(message: SavedWordsCacheBroadcastMessage): void;
  subscribe(listener: (message: SavedWordsCacheBroadcastMessage) => void): () => void;
  close(): void;
}

const cloneRecord = (record: SavedWordsCacheRecord): SavedWordsCacheRecord => {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(record);
  }
  return JSON.parse(JSON.stringify(record)) as SavedWordsCacheRecord;
};

export class MemorySavedWordsCacheStorage implements SavedWordsCacheStorageAdapter {
  private readonly records = new Map<string, SavedWordsCacheRecord>();

  async read(cacheKey: string): Promise<SavedWordsCacheRecord | null> {
    const record = this.records.get(cacheKey);
    return record === undefined ? null : cloneRecord(record);
  }

  async write(record: SavedWordsCacheRecord): Promise<void> {
    this.records.set(record.cacheKey, cloneRecord(record));
  }

  async remove(cacheKey: string): Promise<void> {
    this.records.delete(cacheKey);
  }

  async clear(): Promise<void> {
    this.records.clear();
  }
}

class IndexedDbSavedWordsCacheStorage implements SavedWordsCacheStorageAdapter {
  private databasePromise: Promise<IDBDatabase> | null = null;

  constructor(private readonly indexedDb: IDBFactory) {}

  private openDatabase(): Promise<IDBDatabase> {
    if (this.databasePromise !== null) {
      return this.databasePromise;
    }

    this.databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = this.indexedDb.open(
        SAVED_WORDS_DATABASE_NAME,
        SAVED_WORDS_DATABASE_VERSION,
      );
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(SAVED_WORDS_OBJECT_STORE)) {
          database.createObjectStore(SAVED_WORDS_OBJECT_STORE, { keyPath: 'cacheKey' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
      request.onblocked = () => reject(new Error('IndexedDB open blocked'));
    });
    return this.databasePromise;
  }

  async read(cacheKey: string): Promise<SavedWordsCacheRecord | null> {
    const database = await this.openDatabase();
    return new Promise<SavedWordsCacheRecord | null>((resolve, reject) => {
      const transaction = database.transaction(SAVED_WORDS_OBJECT_STORE, 'readonly');
      const request = transaction.objectStore(SAVED_WORDS_OBJECT_STORE).get(cacheKey);
      request.onsuccess = () => {
        const value: unknown = request.result;
        resolve(value === undefined ? null : value as SavedWordsCacheRecord);
      };
      request.onerror = () => reject(request.error ?? new Error('IndexedDB read failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB read aborted'));
    });
  }

  async write(record: SavedWordsCacheRecord): Promise<void> {
    const database = await this.openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(SAVED_WORDS_OBJECT_STORE, 'readwrite');
      transaction.objectStore(SAVED_WORDS_OBJECT_STORE).put(record);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB write failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB write aborted'));
    });
  }

  async remove(cacheKey: string): Promise<void> {
    const database = await this.openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(SAVED_WORDS_OBJECT_STORE, 'readwrite');
      transaction.objectStore(SAVED_WORDS_OBJECT_STORE).delete(cacheKey);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB delete failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB delete aborted'));
    });
  }

  async clear(): Promise<void> {
    const database = await this.openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(SAVED_WORDS_OBJECT_STORE, 'readwrite');
      transaction.objectStore(SAVED_WORDS_OBJECT_STORE).clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB clear failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB clear aborted'));
    });
  }
}

class ResilientSavedWordsCacheStorage implements SavedWordsCacheStorageAdapter {
  private persistentAvailable = true;

  constructor(
    private readonly persistent: SavedWordsCacheStorageAdapter,
    private readonly fallback: SavedWordsCacheStorageAdapter,
  ) {}

  async read(cacheKey: string): Promise<SavedWordsCacheRecord | null> {
    if (this.persistentAvailable) {
      try {
        const record = await this.persistent.read(cacheKey);
        if (record !== null) {
          await this.fallback.write(record);
          return record;
        }
      } catch {
        this.persistentAvailable = false;
      }
    }
    return this.fallback.read(cacheKey);
  }

  async write(record: SavedWordsCacheRecord): Promise<void> {
    await this.fallback.write(record);
    if (!this.persistentAvailable) {
      return;
    }
    try {
      await this.persistent.write(record);
    } catch {
      this.persistentAvailable = false;
    }
  }

  async remove(cacheKey: string): Promise<void> {
    await this.fallback.remove(cacheKey);
    if (!this.persistentAvailable) {
      return;
    }
    try {
      await this.persistent.remove(cacheKey);
    } catch {
      this.persistentAvailable = false;
    }
  }

  async clear(): Promise<void> {
    await this.fallback.clear();
    if (!this.persistentAvailable) {
      return;
    }
    try {
      await this.persistent.clear();
    } catch {
      this.persistentAvailable = false;
    }
  }
}

class NativeSavedWordsCacheBroadcast implements SavedWordsCacheBroadcastAdapter {
  private readonly listeners = new Set<(message: SavedWordsCacheBroadcastMessage) => void>();

  constructor(private readonly channel: BroadcastChannel) {
    this.channel.onmessage = (event: MessageEvent<unknown>) => {
      const message = event.data;
      if (message === null || typeof message !== 'object' || !('type' in message)) {
        return;
      }
      if (message.type !== 'snapshot' && message.type !== 'clear') {
        return;
      }
      if (
        !('sourceId' in message)
        || typeof message.sourceId !== 'string'
        || (
          message.type === 'clear'
          && (
            !('clearedAtMs' in message)
            || typeof message.clearedAtMs !== 'number'
          )
        )
      ) {
        return;
      }
      for (const listener of this.listeners) {
        listener(message as SavedWordsCacheBroadcastMessage);
      }
    };
  }

  publish(message: SavedWordsCacheBroadcastMessage): void {
    this.channel.postMessage(message);
  }

  subscribe(listener: (message: SavedWordsCacheBroadcastMessage) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  close(): void {
    this.listeners.clear();
    this.channel.close();
  }
}

export const createBrowserSavedWordsCacheStorage = (): SavedWordsCacheStorageAdapter => {
  const fallback = new MemorySavedWordsCacheStorage();
  if (typeof window === 'undefined' || typeof window.indexedDB === 'undefined') {
    return fallback;
  }
  return new ResilientSavedWordsCacheStorage(
    new IndexedDbSavedWordsCacheStorage(window.indexedDB),
    fallback,
  );
};

export const createBrowserSavedWordsCacheBroadcast = (): SavedWordsCacheBroadcastAdapter | null => {
  if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') {
    return null;
  }
  return new NativeSavedWordsCacheBroadcast(
    new window.BroadcastChannel(SAVED_WORDS_BROADCAST_CHANNEL),
  );
};
